import { Checkbox, Input, Scope, useFormApi } from 'informed';
import { useState, useRef, useEffect } from 'react';
import TymberInfoBox from '../TymberInfoBox';
import moment from 'moment';
import { calculateDurationFromWorkTime, calculateWorkTime } from '../../../../utils/time';
import { PrimaryButton } from '@tymbe/ui-primitives';
import { ContentIcon, FavouriteIcon } from '@tymbe/ui-icons';
import DropDownMenu from '../ShiftDetail/DropDownMenu';
import { AttendanceResolution, Roles } from '@tymbe/schema/enums';
import classNames from 'classnames';
import { TyRadioGroup } from '@tymbe/ty-components/inputs/radio';
import { ThumbRadio } from '../ThumbRadio';
import { TyInput, timeFormatter } from '@tymbe/ui-forms';
import Modal from 'react-modal';
import {
  BAD_JOB_EVALUATION_SCORE,
  DEFAULT_JOB_EVALUATION_SCORE,
  GOOD_JOB_EVALUATION_SCORE,
} from '../../../../utils/constants';
import { ApplicationData } from '../../../../types/TymbeApi';
import Protect from '../../../../apiClient/Protect';
import { informedConfig } from '../../../../config/informed';
import { BadgePill } from '@tymbe/ty-components/indicators';
import BlockTymber, { BlockTymberFormValues } from './BlockTymber';
import { useUser } from '../../../../apiClient/ApiContext';
import Spinner from '../../../../components/Spinner';

const DROPDOWN_MENU_OFFSET_X = 85;
const DROPDOWN_MENU_OFFSET_Y = 20;

interface UnapprovedAttendanceRowProps {
  application: ApplicationData;
  submitSingle: (key: number) => Promise<void>;
  startDate: string;
  endDate: string;
  disabled?: boolean;
}

const UnapprovedAttendanceRow = ({
  application,
  submitSingle,
  startDate,
  endDate,
  disabled = false,
}: UnapprovedAttendanceRowProps) => {
  const [openMenu, setOpenMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { person } = useUser();
  const [submitting, setSubmitting] = useState(false);

  const formApi = useFormApi();
  const workedTimeFromStartEnd = (start: string, end: string) => {
    const startMoment = moment(start, 'HH:mm');
    const endMoment = moment(end, 'HH:mm');

    // If end is before or equal to start, assume it's the next day
    if (endMoment.isSameOrBefore(startMoment)) {
      endMoment.add(1, 'day');
    }
    return moment.duration(endMoment.diff(startMoment)).asHours();
  };

  const initialTotalTime = workedTimeFromStartEnd(moment(startDate).format('HH:mm'), moment(endDate).format('HH:mm'));
  const initialWorkedTime = calculateWorkTime(startDate, endDate, false, 'hour');

  const endFromStartAndWorkedTime = (start: string, workedTime: number) => {
    return moment(start, 'HH:mm').add(workedTime, 'hours').format('HH:mm');
  };

  const handleTimeChange = () => {
    const start = formApi.getValue(`applications.${application.id}.start`);
    const end = formApi.getValue(`applications.${application.id}.end`);
    const startMoment = moment(start, 'HH:mm');
    const endMoment = moment(end, 'HH:mm');

    if (endMoment.isSameOrBefore(startMoment)) {
      endMoment.add(1, 'day');
    }

    if (startMoment && endMoment) {
      const workedTime = calculateWorkTime(startMoment, endMoment, false, 'hour');
      const totalTime = workedTimeFromStartEnd(start, end);
      const breakTime = totalTime - workedTime;
      const oldWorkedTime = formApi.getValue(`applications.${application.id}.confirmed_time`);

      if (workedTime === oldWorkedTime || formApi.getFocused(`applications.${application.id}.confirmed_time`)) return;

      formApi.setValue(`applications.${application.id}.confirmed_time`, workedTime);
      formApi.setValue(`applications.${application.id}.break`, breakTime);
    }
  };

  const handleWorkedTimeChange = () => {
    const start = formApi.getValue(`applications.${application.id}.start`);
    const workedTime = formApi.getValue(`applications.${application.id}.confirmed_time`);

    if (start && workedTime != null) {
      const totalWorkTime = calculateDurationFromWorkTime(workedTime * 3600000, false, 'hours');

      const newEnd = endFromStartAndWorkedTime(start, totalWorkTime);
      const breakTime = totalWorkTime - workedTime;
      const oldEnd = formApi.getValue(`applications.${application.id}.end`);

      if (oldEnd === newEnd || formApi.getFocused(`applications.${application.id}.end`)) return;

      formApi.setValue(`applications.${application.id}.end`, newEnd);
      formApi.setValue(`applications.${application.id}.break`, breakTime);
    }
  };

  const handleAttResolution = (resolution: AttendanceResolution) => {
    setSubmitting(true);
    formApi.setValue(`applications.${application.id}.resolution`, resolution);
    submitSingle(application.id).finally(() => setSubmitting(false));
  };

  const confirmAttendance = (id: ApplicationData['id']) => {
    const hasAttendanceDefaultRating =
      formApi.getValue(`applications.${application.id}.rating`) === DEFAULT_JOB_EVALUATION_SCORE.toString();

    // Set rating silently to GOOD_JOB_EVALUATION_SCORE in case of invitation when
    // no rating was set.
    if (hasAttendanceDefaultRating && application.invitation) {
      formApi.setValue(`applications.${application.id}.rating`, GOOD_JOB_EVALUATION_SCORE);
    }
    setSubmitting(true);
    submitSingle(id).finally(() => setSubmitting(false));
  };

  const menuOptions = [
    {
      text: 'Omluvená absence',
      value: AttendanceResolution.EXCUSED_ABSENCE,
      onClick: () => handleAttResolution(AttendanceResolution.EXCUSED_ABSENCE),
    },
    {
      text: 'Neomluvená absence',
      value: AttendanceResolution.UNEXCUSED_ABSENCE,
      onClick: () => handleAttResolution(AttendanceResolution.UNEXCUSED_ABSENCE),
    },
    {
      text: 'Storno',
      value: AttendanceResolution.CANCEL,
      onClick: () => handleAttResolution(AttendanceResolution.CANCEL),
    },
  ];

  const handleButtonClick = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        x: rect.right,
        y: rect.top,
      });
    }
    setOpenMenu(!openMenu);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setOpenMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleBlockTymberSubmit = (blockTymberFormData: BlockTymberFormValues) => {
    const newBlockedPerson = {
      company_id: blockTymberFormData.company_id,
      created_by: person?.id,
      person_id: Number(application?.person_id),
      note: blockTymberFormData.note || null,
      branchoffice_id: blockTymberFormData.branchoffice_id || null,
      reason: blockTymberFormData.reason.value.id,
      severity: Number(blockTymberFormData.reason.value.severity),
    };
    formApi.setValue(`applications.${application.id}.block`, newBlockedPerson);
    setIsBlockTymberModalOpen(false);
    setIsTymberBlocked(true);
  };

  const [isBlockTymberModalOpen, setIsBlockTymberModalOpen] = useState<boolean>(false);
  const [isBlockTymberButtonVisible, setIsBlockTymberButtonVisible] = useState<boolean>(false);
  const [isTymberBlocked, setIsTymberBlocked] = useState<boolean>(false);

  const isUserAlreadyBlocked =
    application.person?.blockedCompany
      ?.filter((company) => company.block_deleted_at === null)
      .find((company) => company.id === application.shift?.company?.id) !== undefined;

  const getBadgeText = () => {
    if (isUserAlreadyBlocked) {
      return 'Uzivatel jiz zabanovan';
    }

    if (isTymberBlocked) {
      return (
        <span className="flex items-center">
          <FavouriteIcon name="close" className="min-w-4 h-4 min-h-4 w-4" />
          Spolupráce bude ukončena
        </span>
      );
    }

    return 'Ukončit spolupráci';
  };

  const handleBadgePillClick = () => {
    if (isUserAlreadyBlocked) {
      return;
    }
    if (formApi.getValue(`applications.${application.id}.block`)) {
      formApi.clearValue(`applications.${application.id}.block`);
      setIsTymberBlocked(false);
    } else {
      setIsBlockTymberModalOpen(true);
    }
  };

  return (
    <>
      <Scope scope={`applications.${application.id}`}>
        <div className="flex flex-col p-4 gap-4 md:grid md:grid-cols-[234px_110px_110px_160px_1fr_182px_160px] md:gap-0">
          {/* profile section */}
          <div className="flex items-center gap-2 md:col-start-1 md:col-span-1">
            <Input
              name="id"
              required={informedConfig.errorMessage.required}
              type="hidden"
              initialValue={application.id}
            />
            <Input name="block" type="hidden" initialValue={undefined} />
            <Input
              name="resolution"
              required={informedConfig.errorMessage.required}
              type="hidden"
              initialValue={AttendanceResolution.OK}
            />
            <Protect
              auth={[
                Roles.SUPER_ADMIN,
                Roles.TYMBE_ADMIN,
                Roles.TYMBE_COORDINATOR,
                Roles.COMPANY,
                Roles.BRANCHOFFICE_MANAGER,
              ]}
              redirect={false}
            >
              <Checkbox className="size-[18px] rounded" name="selected" initialValue={true} />
            </Protect>
            <TymberInfoBox personId={application.person_id} isCompanyJunior={application.isCompanyJunior} />
          </div>

          {/* odpracovano prestavka */}
          <div className="flex gap-5 md:contents">
            <div className="flex flex-col w-1/2 md:col-start-2 md:col-span-1 md:my-auto">
              <div className="text-body-2-md font-normal md:hidden">Odpracováno</div>
              <TyInput
                min="0"
                type="number"
                step={0.01}
                mask={(value: string) => Number(value).toFixed(2)}
                lang="en"
                className="w-full md:w-[90px]"
                name="confirmed_time"
                suffix="h"
                onChange={handleWorkedTimeChange}
                initialValue={initialWorkedTime}
                maskOnBlur
              />
            </div>

            <div className="flex flex-col w-1/2 md:col-start-3 md:col-span-1 md:my-auto">
              <div className="text-body-2-md font-normal md:hidden">Přestávka</div>
              <TyInput
                min="0"
                type="number"
                step={0.01}
                mask={(value: string) => Number(value).toFixed(2)}
                lang="en"
                className="w-full md:w-[75px]"
                name="break"
                suffix="h"
                initialValue={initialTotalTime - initialWorkedTime}
                disabled
              />
            </div>
          </div>

          {/* prichod a odchod */}
          <div className="flex flex-col w-full md:col-start-4 md:col-span-1 md:my-auto md:flex-row md:items-center md:gap-2">
            <div className="text-body-2-md font-normal md:hidden">Příchod a odchod</div>
            <div className="flex gap-2">
              <TyInput
                className="w-16"
                name="start"
                formatter={timeFormatter}
                onChange={handleTimeChange}
                initialValue={moment(startDate).format('H:mm')}
              />
              <div className="w-2 flex items-center">
                <span>-</span>
              </div>
              <TyInput
                className="w-16"
                name="end"
                formatter={timeFormatter}
                onChange={handleTimeChange}
                initialValue={moment(endDate).format('H:mm')}
              />
            </div>
          </div>

          <Protect
            auth={[
              Roles.SUPER_ADMIN,
              Roles.TYMBE_ADMIN,
              Roles.TYMBE_COORDINATOR,
              Roles.COMPANY,
              Roles.BRANCHOFFICE_MANAGER,
            ]}
            redirect={false}
          >
            <div className="col-start-6 row-start-1 mx-auto mt-2">
              <div className="flex justify-center items-center gap-2">
                <TyRadioGroup
                  name="rating"
                  defaultValue={`${DEFAULT_JOB_EVALUATION_SCORE}`}
                  className="flex items-center gap-2"
                  onChange={(value) => {
                    setIsBlockTymberButtonVisible(value.value !== BAD_JOB_EVALUATION_SCORE.toString() ? false : true);
                    if (value.value === GOOD_JOB_EVALUATION_SCORE.toString()) {
                      formApi.clearValue(`applications.${application.id}.block`);
                      setIsBlockTymberButtonVisible(false);
                      setIsTymberBlocked(false);
                    }
                  }}
                >
                  <ThumbRadio variant="down" className="size-[30px]" size={25} value={`${BAD_JOB_EVALUATION_SCORE}`} />
                  <ThumbRadio variant="up" className="size-[30px]" size={25} value={`${GOOD_JOB_EVALUATION_SCORE}`} />
                </TyRadioGroup>
              </div>
            </div>

            {/* block tymber modal actuation badge pill */}
            <div
              className={classNames('col-start-6 row-start-2 mx-auto mt-2', { hidden: !isBlockTymberButtonVisible })}
            >
              <BadgePill
                variant={isUserAlreadyBlocked ? 'warning' : 'error'}
                significant={isTymberBlocked}
                className="w-fit cursor-pointer"
                onClick={handleBadgePillClick}
              >
                {getBadgeText()}
              </BadgePill>
            </div>
          </Protect>

          {/* attendance rating and actions */}
          <Protect
            auth={[
              Roles.SUPER_ADMIN,
              Roles.TYMBE_ADMIN,
              Roles.TYMBE_COORDINATOR,
              Roles.COMPANY,
              Roles.BRANCHOFFICE_MANAGER,
            ]}
            redirect={false}
          >
            <div className="flex flex-col gap-2 md:col-start-7 md:col-span-1 md:flex-row md:justify-end md:items-center md:gap-2">
              <div className="flex justify-center items-center gap-2">
                <PrimaryButton
                  className="flex gap-2 items-center h-10 flex-grow md:flex-none"
                  onClick={() => {
                    confirmAttendance(application.id);
                  }}
                  disabled={submitting}
                >
                  {submitting ? (
                    <Spinner show className="size-[18px]" />
                  ) : (
                    <FavouriteIcon name="check" className="size-[18px] min-h-[18px] min-w-[18px]" />
                  )}
                  Potvrdit
                </PrimaryButton>

                <button
                  ref={buttonRef}
                  id={`application-${application.id}`}
                  className={classNames(
                    'rounded-full border border-red-300 size-10 p-0 flex justify-center min-w-[40px]',
                    'items-center text-red-500 disabled:text-secondary-400 disabled:border-secondary-200'
                  )}
                  onClick={handleButtonClick}
                  disabled={submitting}
                >
                  {submitting ? (
                    <Spinner show className="size-[18px]" />
                  ) : (
                    <ContentIcon name="block" className="size-[18px]" />
                  )}
                  {openMenu && (
                    <DropDownMenu
                      className="z-50 fixed"
                      style={{
                        left: `${menuPosition.x - DROPDOWN_MENU_OFFSET_X}px`,
                        top: `${menuPosition.y - DROPDOWN_MENU_OFFSET_Y}px`,
                      }}
                      options={menuOptions}
                      onBlur={() => setOpenMenu(false)}
                    />
                  )}
                </button>
              </div>
            </div>
          </Protect>
        </div>
      </Scope>
      <Modal
        isOpen={isBlockTymberModalOpen}
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEsc={false}
        ariaHideApp={false}
        className="fixed top-[25%] right-[25%] w-[50%] max-h-[70%] bg-bg rounded-lg drop-shadow-lg z-50 flex flex-col"
        overlayClassName="bg-overlay fixed inset-0 z-modal"
      >
        <div className="text-secondary-600 text-heading-4-xl p-4 flex align-center border-b border-secondary-100">
          Ukončit spolupráci
          <FavouriteIcon
            name="close"
            onClick={() => setIsBlockTymberModalOpen(false)}
            className="size-5 cursor-pointer ml-auto"
          />
        </div>
        <BlockTymber
          onSubmit={handleBlockTymberSubmit}
          applicationId={application.id}
          setModalOpen={setIsBlockTymberModalOpen}
        />
      </Modal>
    </>
  );
};

export default UnapprovedAttendanceRow;
